/* eslint-disable indent, no-param-reassign,no-plusplus, no-unused-vars */
import axios from 'axios';
import Vue from 'vue';
import sortBy from 'lodash/sortBy';
import groupBy from 'lodash/groupBy';
import uniq from 'lodash/uniq';
import cacheAxios from '../../util/cacheAxios';


const relocationAxios = axios.create();
const cache = new Map();
cacheAxios(relocationAxios, cache);

relocationAxios.interceptors.response.use(response => response,
  (error) => {
    if (error && error.response && error.response.status === 401) {
      setTimeout(() => {
        window.location.href = '/sign/in?source=/aims#/costofliving';
      }, 500);
    }
    throw error;
  });

export default {
  state: {
    currencySelected: 'USD',
    salaryCurrency: 'USD',
    tax: {
      home: {},
      destination: {},
    },
    spendingMultipliers: {
      hi: [],
      mid: [],
      low: [],
    },
    hardshipRank: {
      home: {},
      destination: {},
    },
    medicalCost: {
      home: {},
      destination: {},
    },
    cities: [],
    relocationDetails: {
      relocationTypeChanged: false,
      relocationType: '',
      homeCity: {},
      destinationCity: {},
    },
    employeeInfo: {
      annualSalary: '',
      annualBonus: '',
      maritalStatus: 'married',
      noOfKids: '',
      age: '',
    },
    homeCityPrices: {},
    destinationCityPrices: {},
    allowance: {
      housingCost: {
        selected: false,
        value: -1,
        diffOrFull: '',
        option: '',
      },
      educationCost: {
        selected: false,
        value: -1,
        diffOrFull: '',
        option: '',
      },
      carCost: {
        selected: false,
        value: -1,
        diffOrFull: '',
        option: '',
      },
      livingCost: {
        selected: false,
        value: -1,
        diffOrFull: '',
        option: '',
      },
      medicalCost: {
        selected: false,
        value: -1,
        diffOrFull: '',
        option: '',
      },
      riskAllowance: {
        selected: false,
        value: -1,
        diffOrFull: '',
        option: '',
      },
      oneTimeAllowance: {
        selected: false,
        value: -1,
        diffOrFull: '',
        option: '',
      },
      locationPreviewAllowance: {
        selected: false,
        value: -1,
        diffOrFull: '',
        option: '',
      },
      taxAllowance: {
        selected: false,
        value: -1,
        diffOrFull: '',
        option: '',
      },
    },
    citiesTrends: [],
    countriesTrends: [],
  },
  mutations: {
    setCitiesTrends(state, trends) {
      state.citiesTrends = trends;
    },
    setCountriesTrends(state, trends) {
      state.countriesTrends = trends;
    },
    setRelocationSalaryCurrency(state, cur) {
      state.salaryCurrency = cur;
    },
    setRelocationCurrencySelected(state, cur) {
      state.currencySelected = cur;
    },
    setAllowance(state, {
      type,
      value,
      details,
      diffOrFull,
      option,
      selected,
      homePrice,
      destinationPrice,
    }) {
      Vue.set(state.allowance, type, {
        value,
        details,
        diffOrFull,
        option,
        selected,
        homePrice,
        destinationPrice,
      });
    },
    setResidentHomeTax(state, tax) {
      Vue.set(state.tax.home, 'resident', tax);
    },
    setNonResidentHomeTax(state, tax) {
      Vue.set(state.tax.home, 'nonResident', tax);
    },
    setNonResidentDestinationTax(state, tax) {
      Vue.set(state.tax.destination, 'nonResident', tax);
    },
    setResidentDestinationTax(state, tax) {
      Vue.set(state.tax.destination, 'resident', tax);
    },
    setHomeHardshipRank(state, rank) {
      Vue.set(state.hardshipRank, 'home', rank);
    },
    setDestinationHardshipRank(state, rank) {
      Vue.set(state.hardshipRank, 'destination', rank);
    },
    setHomeMedicalCost(state, costs) {
      Vue.set(state.medicalCost, 'home', costs);
    },
    setDestinationMedicalCost(state, costs) {
      Vue.set(state.medicalCost, 'destination', costs);
    },
    setSpendingMultipliers(state, multipliers) {
      state.spendingMultipliers = multipliers;
    },
    setHomeCityPrices(state, prices) {
      state.homeCityPrices = prices;
    },
    setDestinationCityPrices(state, prices) {
      state.destinationCityPrices = prices;
    },
    setCostOfLivingCities(state, cities) {
      state.cities = cities;
    },
    setRelocationType(state, type) {
      if (type !== state.relocationDetails.relocationType) {
        Vue.set(state.relocationDetails, 'relocationType', type);
        Vue.set(state.relocationDetails, 'relocationTypeChanged', true);
      }
    },
    resetRelocationType(state) {
      Vue.set(state.relocationDetails, 'relocationTypeChanged', false);
    },
    setHomeCity(state, city) {
      Vue.set(state.relocationDetails, 'homeCity', city);
    },
    setDestinationCity(state, city) {
      Vue.set(state.relocationDetails, 'destinationCity', city);
    },
    setEmployeeAge(state, age) {
      Vue.set(state.employeeInfo, 'age', age);
    },
    setEmployeeNoOfKids(state, noOfKids) {
      if (noOfKids === '') {
        Vue.set(state.employeeInfo, 'noOfKids', '');
        return;
      }
      Vue.set(state.employeeInfo, 'noOfKids', +noOfKids);
    },
    setEmployeeMaritalStatus(state, maritalStatus) {
      Vue.set(state.employeeInfo, 'maritalStatus', maritalStatus);
    },
    setEmployeeAnnualBonus(state, annualBonus) {
      if (annualBonus === '') {
        Vue.set(state.employeeInfo, 'annualBonus', '');
        return;
      }
      Vue.set(state.employeeInfo, 'annualBonus', +annualBonus);
    },
    setEmployeeAnnualSalary(state, annualSalary) {
      if (annualSalary === '') {
        Vue.set(state.employeeInfo, 'annualSalary', '');
        return;
      }
      Vue.set(state.employeeInfo, 'annualSalary', +annualSalary);
    },
  },
  actions: {
    getCitiesTrends({
      commit,
    }) {
      relocationAxios.get('/aims/api/costofliving/citiesTrends')
        .then((res) => {
          if (res.data) {
            commit('setCitiesTrends', res.data);
          }
        });
    },
    getCountriesTrends({
      commit,
    }) {
      relocationAxios.get('/aims/api/costofliving/countriesTrends')
        .then((res) => {
          if (res.data) {
            commit('setCountriesTrends', res.data);
          }
        });
    },
    getCostOfLivingCities({
      commit,
    }) {
      relocationAxios.get('/aims/api/costofliving/cities')
        .then((res) => {
          if (res.data) {
            commit('setCostOfLivingCities', res.data);
          }
        });
    },
    getTax({
      commit,
      state,
      getters,
    }) {
      const homeRate = state.homeCityPrices.usdDivisor;
      const destinationRate = state.destinationCityPrices.usdDivisor;
      const salaryTotal = state.employeeInfo.annualBonus + state.employeeInfo.annualSalary;
      relocationAxios.get(
        `/aims/api/costofliving/calcTaxUsd/${state.relocationDetails.homeCity.country}/${((salaryTotal / getters.salaryRate.usdDivisor)).toFixed(3)}/resident`,
      )
        .then((res) => {
          if (res && res.data !== null) {
            commit('setResidentHomeTax', res.data);
          } else {
            commit('setResidentHomeTax', undefined);
          }
        })
        .catch(err => commit('setHomeTax', undefined));
      relocationAxios.get(
        `/aims/api/costofliving/calcTaxUsd/${state.relocationDetails.homeCity.country}/${((salaryTotal / getters.salaryRate.usdDivisor)).toFixed(3)}/non-resident`,
      )
        .then((res) => {
          if (res && res.data !== null) {
            commit('setNonResidentHomeTax', res.data);
          } else {
            commit('setNonResidentHomeTax', undefined);
          }
        })
        .catch(err => commit('setHomeTax', undefined));
      relocationAxios.get(
        `/aims/api/costofliving/calcTaxUsd/${state.relocationDetails.destinationCity.country}/${((salaryTotal / getters.salaryRate.usdDivisor)).toFixed(3)}/non-resident`,
      )
        .then((res) => {
          if (res && res.data !== null) {
            commit('setNonResidentDestinationTax', res.data);
          } else {
            commit('setNonResidentDestinationTax', undefined);
          }
        })
        .catch((err) => {
          commit('setDestinationTax', undefined);
        });
      relocationAxios.get(
        `/aims/api/costofliving/calcTaxUsd/${state.relocationDetails.destinationCity.country}/${((salaryTotal / getters.salaryRate.usdDivisor)).toFixed(3)}/resident`,
      )
        .then((res) => {
          if (res && res.data !== null) {
            commit('setResidentDestinationTax', res.data);
          } else {
            commit('setResidentDestinationTax', undefined);
          }
        })
        .catch((err) => {
          commit('setDestinationTax', undefined);
        });
    },
    getRelocationCosts({
      state,
      commit,
    }) {
      const home = relocationAxios.get(
        `/aims/api/costofliving/allCosts/${state.relocationDetails.homeCity.country}/${state.relocationDetails.homeCity.city}`,
      ).then((res) => {
        if (res.data) {
          commit('setHomeCityPrices', res.data);
        } else {
          commit('setHomeCityPrices', {});
          throw Error('failed to get home city prices');
        }
      });
      const destination = relocationAxios.get(
        `/aims/api/costofliving/allCosts/${state.relocationDetails.destinationCity.country}/${state.relocationDetails.destinationCity.city}`,
      ).then((res) => {
        if (res.data) {
          commit('setDestinationCityPrices', res.data);
        } else {
          commit('setDestinationCityPrices', {});
          throw Error('failed to get detination city prices');
        }
      });
      return Promise.all([home, destination]);
    },
    getHardshipRank({
      state,
      commit,
    }) {
      relocationAxios.get(
        `/aims/api/costofliving/hardship/${state.relocationDetails.homeCity.country}/${state.relocationDetails.homeCity.city}`,
      )
        .then((res) => {
          if (res && res.data) {
            commit('setHomeHardshipRank', res.data);
          } else {
            commit('setHomeHardshipRank', {});
          }
        })
        .catch((err) => {
          commit('setHomeHardshipRank', {});
        });
      relocationAxios.get(
        `/aims/api/costofliving/hardship/${state.relocationDetails.destinationCity.country}/${state.relocationDetails.destinationCity.city}`,
      )
        .then((res) => {
          if (res && res.data) {
            commit('setDestinationHardshipRank', res.data);
          } else {
            commit('setDestinationHardshipRank', {});
          }
        }).catch((err) => {
          commit('setDestinationHardshipRank', {});
        });
    },
    getSpendingMultiplier({
      commit,
    }) {
      relocationAxios.get('/aims/api/costofliving/spendingMultipliers')
        .then((res) => {
          if (res.data) {
            commit('setSpendingMultipliers', res.data);
          }
        });
    },
    getMedicalCost({
      commit,
      state,
    }) {
      relocationAxios.get(
        `/aims/api/costofliving/medicalCost/${state.relocationDetails.homeCity.country}/${state.employeeInfo.age}`,
      )
        .then((res) => {
          if (res.data) {
            commit('setHomeMedicalCost', res.data);
          } else {
            commit('setHomeMedicalCost', {});
          }
        })
        .catch(err => commit('setHomeMedicalCost', {}));
      relocationAxios.get(
        `/aims/api/costofliving/medicalCost/${state.relocationDetails.destinationCity.country}/${state.employeeInfo.age}`,
      )
        .then((res) => {
          if (res.data) {
            commit('setDestinationMedicalCost', res.data);
          } else {
            commit('setDestinationMedicalCost', {});
          }
        })
        .catch(err => commit('setDestinationMedicalCost', {}));
    },
  },
  getters: {
    citiesTree(state) {
      if (!state.cities) {
        return [];
      }
      const regions = sortBy(state.cities, 'region');
      return regions
        .map(region => ({
          label: region.region,
          value: region.region,
          children: sortBy(region.countries, 'country')
            .map(country => ({
              label: country.country,
              value: country.country,
              children: sortBy(country.cities).map(city => ({
                label: city,
                value: city,
              })),
            })),
        }));
    },
    trendsTree(state) {
      if (!state.citiesTrends || !state.countriesTrends ||
        state.citiesTrends.length === 0 || state.countriesTrends.length === 0) {
        return [];
      }
      const regionCountry = groupBy(state.countriesTrends, 'region');
      const regionCities = groupBy(state.citiesTrends, 'region');
      const regions = uniq([...Object.keys(regionCountry), ...Object.keys(regionCities)]);
      regions.sort();
      return regions.map((r) => {
        const countryCities = groupBy(regionCities[r], 'country');
        const coCountries = regionCountry[r].map(co => co.country);
        const cityCountries = Object.keys(countryCities);
        const countries = uniq([...coCountries, ...cityCountries]);
        countries.sort();
        const children = countries.map((c) => {
          const countryItems = regionCountry[r].find(co => co.country === c);
          const value = countryItems || c;
          const cities = countryCities[c];
          if (cities) {
            const cityChildren = cities ? cities.map(ci => ({
              label: ci.city,
              value: ci,
            })) : undefined;
            return {
              label: c,
              value,
              children: sortBy(cityChildren, 'city'),
            };
          }
          return {
            label: c,
            value,
          };
        });
        return {
          label: r,
          value: r,
          children,
        };
      });
    },
    taxConverted(state, getter) {
      return {
        home: {
          resident: state.tax.home.resident * getter.selectedRate.usdDivisor,
          nonResident: state.tax.home.nonResident * getter.selectedRate.usdDivisor,
        },
        destination: {
          resident: state.tax.destination.resident * getter.selectedRate.usdDivisor,
          nonResident: state.tax.destination.nonResident * getter.selectedRate.usdDivisor,
        },
      };
    },
    selectedRate(state, getters, rootState) {
      return rootState.currencyExchangeRates
        .find(c => c.currencyCode === state.currencySelected) || {
          usdDivisor: 1,
        };
    },
    salaryRate(state, getters, rootState) {
      return rootState.currencyExchangeRates
        .find(c => c.currencyCode === state.salaryCurrency) || {
          usdDivisor: 1,
        };
    },
    homeCityPriceIdMap(state) {
      if (!state.homeCityPrices.prices) return {};
      const priceMap = {};
      state.homeCityPrices.prices.forEach((p) => {
        let price = p.average_price * 1.1;
        if (p.item_name.startsWith('Apartment')) {
          let m = 1.9;
          if (state.homeCityPrices.continent === 'Asia') {
            m = 1.9;
          } else {
            m = 2.4;
          }
          price = p.average_price * m;
        }
        priceMap[p.item_id] = {
          price,
          item: p.item_name,
        };
      });
      return priceMap;
    },
    destinationCityPriceIdMap(state) {
      if (!state.destinationCityPrices.prices) return {};
      const priceMap = {};
      state.destinationCityPrices.prices.forEach((p) => {
        let price = p.average_price * 1.1;
        if (p.item_name.startsWith('Apartment')) {
          let m = 1.9;
          if (state.destinationCityPrices.continent === 'Asia') {
            m = 1.9;
          } else {
            m = 2.4;
          }
          price = p.average_price * m;
        }
        priceMap[p.item_id] = {
          price,
          item: p.item_name,
        };
      });
      return priceMap;
    },
    homeCityPriceMap(state) {
      if (!state.homeCityPrices.prices) return {};
      const city = {
        ...state.homeCityPrices,
      };
      const priceMap = {};
      city.prices.forEach((p) => {
        let price = p.average_price * 1.1;
        if (p.item_name.startsWith('Apartment')) {
          let m = 1.9;
          if (city.continent === 'Asia') {
            m = 1.9;
          } else {
            m = 2.4;
          }
          price = p.average_price * m;
        }
        priceMap[p.item_name] = price;
      });
      city.prices = priceMap;
      return city;
    },
    destinationCityPriceMap(state) {
      if (!state.destinationCityPrices.prices) return {};
      const city = {
        ...state.destinationCityPrices,
      };
      const priceMap = {};
      city.prices.forEach((p) => {
        let price = p.average_price * 1.1;
        if (p.item_name.startsWith('Apartment')) {
          let m = 1.9;
          if (city.continent === 'Asia') {
            m = 1.9;
          } else {
            m = 2.4;
          }
          price = p.average_price * m;
        }
        priceMap[p.item_name] = price;
      });
      city.prices = priceMap;
      return city;
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/relocationModule.js